import { renderStreamMessage } from "@hotwired/turbo"

export const OFFLINE_WRITE_MESSAGE = "Geen verbinding. Probeer opnieuw zodra je weer online bent."

export function notifyOfflineWrite(message = OFFLINE_WRITE_MESSAGE) {
  alert(message)
}

export function csrfToken() {
  const el = document.querySelector('meta[name="csrf-token"]')
  return el ? el.getAttribute("content") : ""
}

export function apiHeaders(extra = {}) {
  return Object.assign({
    "Content-Type": "application/json",
    "Accept": "application/json",
    "X-CSRF-Token": csrfToken()
  }, extra)
}

export function htmlHeaders(frameId = null) {
  const headers = {
    "Accept": "text/html",
    "X-Requested-With": "XMLHttpRequest"
  }
  if (frameId) headers["Turbo-Frame"] = frameId
  return headers
}

export async function requestJson(url, options = {}) {
  setSaveStatus("Opslaan...")
  const response = await writeFetch(url, {
    credentials: "include",
    ...options,
    headers: apiHeaders(options.headers || {})
  })

  if (!response.ok) {
    setSaveStatus(await responseErrorMessage(response, "Opslaan mislukt"), false)
    throw new Error(`Sweeper editor request failed: ${response.status}`)
  }

  const session = await response.json()
  notifySessionUpdated(session)
  setSaveStatus(`Opgeslagen ${formatDate(new Date().toISOString())}`, true)
  return session
}

export function patchJson(url, body) {
  return requestJson(url, {
    method: "PATCH",
    body: JSON.stringify(body)
  })
}

export function postJson(url, body) {
  return requestJson(url, {
    method: "POST",
    body: JSON.stringify(body)
  })
}

export function deleteJson(url) {
  return requestJson(url, { method: "DELETE" })
}

export async function streamRequest(url, options = {}) {
  setSaveStatus("Opslaan...")
  const response = await writeFetch(url, {
    credentials: "include",
    ...options,
    headers: apiHeaders(Object.assign({}, options.headers || {}, {
      "Accept": "text/vnd.turbo-stream.html, application/json"
    }))
  })

  if (!response.ok) {
    setSaveStatus(await responseErrorMessage(response, "Opslaan mislukt"), false)
    throw new Error(`Sweeper editor request failed: ${response.status}`)
  }

  const contentType = response.headers.get("Content-Type") || ""
  if (contentType.includes("text/vnd.turbo-stream.html")) {
    renderStreamMessage(await response.text())
    updateLastSaved()
  } else {
    notifySessionUpdated(await response.json())
  }

  setSaveStatus(`Opgeslagen ${formatDate(new Date().toISOString())}`, true)
}

export function patchStream(url, body) {
  return streamRequest(url, {
    method: "PATCH",
    body: JSON.stringify(body)
  })
}

export function postStream(url, body) {
  return streamRequest(url, {
    method: "POST",
    body: JSON.stringify(body)
  })
}

export function deleteStream(url) {
  return streamRequest(url, { method: "DELETE" })
}

export async function writeFetch(url, options = {}) {
  if (navigator.onLine === false) return offlineWriteResponse()

  try {
    return await fetch(url, options)
  } catch {
    return offlineWriteResponse()
  }
}

export function offlineWriteResponse() {
  return new Response(JSON.stringify({ error: OFFLINE_WRITE_MESSAGE }), {
    status: 503,
    headers: { "Content-Type": "application/json" }
  })
}

export async function responseErrorMessage(response, fallback) {
  const error = await response.clone().json().catch(() => ({}))
  return error.error || fallback
}

export function sessionIdFor(element) {
  const frame = element.closest("[data-session-id-value]")
  if (frame?.dataset.sessionIdValue) return frame.dataset.sessionIdValue

  const content = document.getElementById("editor-content")
  return content?.dataset.sessionId || ""
}

export function debounce(instance, key, callback, delay = 450) {
  instance._sweeperDebounces ||= {}
  clearTimeout(instance._sweeperDebounces[key])
  instance._sweeperDebounces[key] = setTimeout(callback, delay)
}

export function syncRiskCard(card, risk) {
  if (!card) return
  card.classList.remove("has-risk-low", "has-risk-mid", "has-risk-high")
  if (risk) card.classList.add(`has-risk-${risk}`)

  card.querySelectorAll(".risk-row label").forEach(label => label.classList.remove("checked", "low", "mid", "high"))
  card.querySelectorAll(".risk-row input").forEach(input => { input.checked = false })
  const checked = card.querySelector(`.risk-row input[value="${risk}"]`)
  if (checked) {
    checked.checked = true
    checked.parentElement.classList.add("checked", risk)
  }
}

export function setSaveStatus(text, saved = false) {
  const status = document.getElementById("save-status")
  if (!status) return

  status.textContent = text
  status.classList.toggle("saved", saved)
}

export function updateLastSaved(iso = new Date().toISOString()) {
  const timestamp = iso || new Date().toISOString()
  document.querySelectorAll("[data-last-saved]").forEach(element => {
    element.textContent = `Laatst opgeslagen ${formatDate(timestamp)}`
  })
}

export function notifySessionUpdated(session) {
  if (!session?.id) return

  document.dispatchEvent(new CustomEvent("sweeper:session-updated", {
    detail: { session }
  }))
  updateLastSaved(session.updatedAt)
}

export function formatDate(iso) {
  const d = new Date(iso)
  return d.toLocaleString("nl-NL", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })
}

export function parseIntOrNull(value) {
  if (value === "" || value == null) return null
  const parsed = parseInt(value, 10)
  return Number.isFinite(parsed) ? parsed : null
}

export function showFormError(id, message) {
  const el = document.getElementById(id)
  if (!el) return
  el.textContent = message
  el.classList.remove("hidden")
}
